import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiPlus } from "react-icons/fi";
import api from "../../../configs/api";
import { showError, showSuccess } from "../../../utils/toastUtils";

const EMPTY_FORM = { name: "", unit: "kg", plant: "" };

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // for edit

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [varieties, setVarieties] = useState([{ name: "", price: "" }]);
  const [gradeMixtures, setGradeMixtures] = useState([]);

  const [grades, setGrades] = useState([]);
  const [plants, setPlants] = useState([]);

  /* FETCH DATA */
  useEffect(() => {
    fetchPlants();
    fetchGrades();
    if (id) fetchProduct();
  }, [id]);

  const fetchPlants = async () => {
    try {
      const res = await api.get("/plants");
      setPlants(res.data.data);
    } catch {
      showError("Failed to load plants");
    }
  };

  const fetchGrades = async () => {
    try {
      const res = await api.get("/grade-mixtures/");
      setGrades(res.data.data);
    } catch {
      showError("Failed to load grades");
    }
  };

  const fetchProduct = async () => {
    try {
      const res = await api.get(`/calculator-products/${id}`);
      const product = res.data.data;

      setForm({
        name: product.name,
        unit: product.unit || "kg",
        plant: product.plant?._id || "",
      });

      setVarieties(
        product.variety?.length ? product.variety : [{ name: "", price: "" }],
      );

      setGradeMixtures(
        product.gradeMixtures?.length
          ? product.gradeMixtures.map((g) => ({
              grade: g.grade?._id || g.grade,
              quantity: g.quantity,
            }))
          : [],
      );
    } catch {
      showError("Failed to load product");
    }
  };

  /* SAVE */
  const handleSave = async () => {
    if (!form.name.trim()) return showError("Product name is required");

    const cleanedGrades = gradeMixtures.filter((g) => g.grade && g.quantity);

    const payload = {
      ...form,
      variety: varieties,
      gradeMixtures: cleanedGrades,
    };

    try {
      setSaving(true);

      if (id) {
        await api.put(`/calculator-products/${id}`, payload);
        showSuccess("Product updated");
      } else {
        await api.post(`/calculator-products`, payload);
        showSuccess("Product created");
      }

      // navigate("/admin/admin-calculator");
    } catch {
      showError("Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  /* VARIETY */
  const handleVarietyChange = (i, field, value) => {
    const updated = [...varieties];
    updated[i][field] = value;
    setVarieties(updated);
  };

  const addVariety = () =>
    setVarieties([...varieties, { name: "", price: "" }]);

  const removeVariety = (i) =>
    setVarieties(varieties.filter((_, idx) => idx !== i));

  /* GRADES */
  const handleGradeChange = (i, field, value) => {
    const updated = [...gradeMixtures];
    updated[i][field] = value;
    setGradeMixtures(updated);
  };

  const addGrade = () =>
    setGradeMixtures([...gradeMixtures, { grade: "", quantity: "" }]);

  const removeGrade = (i) =>
    setGradeMixtures(gradeMixtures.filter((_, idx) => idx !== i));

  const selectedGrades = gradeMixtures.map((g) => g.grade);

  return (
    <div className="p-6 w-full">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-semibold">
          {id ? "Edit Product" : "Add Product"}
        </h1>

        <button onClick={() => navigate(-1)} className="text-sm text-gray-500">
          Back
        </button>
      </div>

      {/* MAIN LAYOUT */}
      <div className="flex gap-6 w-full">
        {/* LEFT SIDE */}
        <div className="flex-1 bg-white p-6 border shadow-sm space-y-5">
          {/* PLANT */}
          <div>
            <label className="text-xs">Plant Name*</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {plants.map((p) => {
                const isSelected = form.plant === p._id;

                return (
                  <div
                    key={p._id}
                    onClick={() => setForm({ ...form, plant: p._id })}
                    className={`px-3 py-2 border  cursor-pointer text-sm transition
          ${
            isSelected
              ? "bg-primaryAccent text-white border-primaryAccent"
              : "bg-gray-100 hover:bg-gray-200"
          }`}
                  >
                    {p.plantName}
                  </div>
                );
              })}
            </div>
          </div>

          {/* NAME */}
          <div>
            <label className="text-xs">Product Name *</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border px-3 py-2"
            />
          </div>

          {/* UNIT */}
          <div>
            <label className="text-xs">Unit</label>
            <select
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              className="w-full border px-3 py-2"
            >
              <option value="kg">kg</option>
              <option value="g">g</option>
              <option value="l">l</option>
              <option value="ml">ml</option>
            </select>
          </div>

          {/* VARIETIES */}
          <div>
            <label className="text-xs mb-2 block">Varieties</label>

            {varieties.map((v, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  placeholder="Name"
                  value={v.name}
                  onChange={(e) =>
                    handleVarietyChange(i, "name", e.target.value)
                  }
                  className="flex-1 border px-2 py-2"
                />
                <input
                  placeholder="Price"
                  value={v.price}
                  onChange={(e) =>
                    handleVarietyChange(i, "price", e.target.value)
                  }
                  className="w-24 border px-2 py-2"
                />
                <button onClick={() => removeVariety(i)}>✕</button>
              </div>
            ))}

            <button
              onClick={addVariety}
              className="text-sm text-btnColor flex items-center gap-1"
            >
              <FiPlus /> Add Variety
            </button>
          </div>

          {/* ACTION */}
        </div>

        {/* RIGHT SIDE (GRADES) */}
        <div className="w-[400px] bg-white p-6 border shadow-sm">
          <label className="text-xs mb-3 block font-medium">Grades</label>

          {/* ALL GRADES SELECTOR */}
          <div className="flex flex-wrap gap-2 mb-4 max-h-32 overflow-y-auto">
            {grades.map((gr) => {
              const isSelected = gradeMixtures.some((g) => g.grade === gr._id);

              return (
                <div
                  key={gr._id}
                  onClick={() => {
                    if (isSelected) return; // prevent duplicate
                    setGradeMixtures([
                      ...gradeMixtures,
                      { grade: gr._id, quantity: "" },
                    ]);
                  }}
                  className={`px-3 py-1 border rounded cursor-pointer text-sm transition
            ${
              isSelected
                ? "bg-primaryAccent text-white border-primaryAccent cursor-not-allowed"
                : "bg-gray-100 hover:bg-gray-200"
            }`}
                >
                  {gr.gradeName}
                </div>
              );
            })}
          </div>

          {/* SELECTED GRADES WITH QUANTITY */}
          <div className="flex flex-wrap gap-2 w-full">
            {gradeMixtures.map((g, i) => {
              const gradeData = grades.find((gr) => gr._id === g.grade);

              return (
                <div
                  key={i}
                  className="flex flex-wrap items-center border rounded px-2 py-1 bg-gray-50 "
                >
                  {/* Grade Name */}
                  <span className="text-xs font-medium">
                    {gradeData?.gradeName || "Grade"}
                  </span>

                  {/* Quantity */}
                  <input
                    placeholder="Qty"
                    value={g.quantity}
                    onChange={(e) =>
                      handleGradeChange(i, "quantity", e.target.value)
                    }
                    className="w-14 border px-1 py-0.5 text-xs rounded"
                  />

                  {/* Remove */}
                  <button
                    onClick={() => removeGrade(i)}
                    className="text-red-500 text-xs px-1"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>

          {/* 🔹 OPTIONAL ADD BUTTON */}
          {/* <button
            onClick={() =>
              setGradeMixtures([...gradeMixtures, { grade: "", quantity: "" }])
            }
            className="text-sm text-btnColor flex items-center gap-1 mt-4"
          >
            <FiPlus /> Add Grade
          </button> */}
        </div>
      </div>
      <div className="mt-6 flex items-center justify-center">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-primaryAccent text-white px-6 py-2 rounded"
        >
          {saving ? "Saving..." : "Save Product"}
        </button>
      </div>
    </div>
  );
};

export default ProductForm;
